import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { Moment } from "moment-timezone";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { IconCalendarPlus, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { castPeriod, castPrice, toGQLPeriod } from "@ct-react/core";
import { BookingConfig, BookingOption, calculateBookingOptionWithExtras, castBookingOption } from "@ct-react/calendar";
import { useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { ArticleType } from "@shared/url";
import { AgeCategory, CartOptionType, RentalPrice, TouristTaxCartOption } from "@shared/graphql";
import { actions, booking, form, pricing } from "../../../i18n/sharable-defs";
import { DataProps } from "../../../tools/components";
import * as checkoutTools from "../../../tools/search-params/checkout";
import { CustomRentalAccommodationFilterInput, useSearchContext } from "../../../contexts/search";
import { usePeriodParams } from "../../../hooks/query-params";
import { useTools } from "../../../hooks/tools";
import { Overlay } from "../../layout/overlays";
import { Display } from "../../layout/displays";
import Button from "../../common/button";
import PeriodInput from "./booking-inputs/period";
import GuestsInput from "./booking-inputs/guests";
import ContactForm from "../../contact-form";
import "./booking-box.scss";

const transDefs = defineMessages({
  see: { id: "article.aside-booking.see-availabilities", defaultMessage: "Voir les disponibilités" },
  contact: { id: "article.aside-booking.contact", defaultMessage: "Contacter l'agence" },
  invalid: { id: "article.aside-booking.invalid-help", defaultMessage: "Remplissez tous les critères ci-dessus pour pouvoir réserver." },
  onDemand: { id: "article.aside-booking.on-demand-help", defaultMessage: "Disponible sur demande uniquement. Faites suite en contactant l'agence directement." },
});

const PRICE_GQL_DATA = gql`
  query BookingPrice($articleId: ID!, $period: DatePeriod!) {
    option: bookingAccommodationPriceDetail(articleId: $articleId, period: $period) {
      period bookable priceOnDemand
      price { amount detailed { bare discount providerCharge { amount adaptive } } }
    }
  }
`;

type ContentRender = { title: string, children: ReactNode };

type InnerProps = {
  articleId: string;
  lowestPrice?: RentalPrice;
  config?: BookingConfig<Moment>;
  touristTaxConfig?: TouristTaxCartOption;
  maxGuests?: number;
}

type Props = DataProps<InnerProps> & {
  activator: boolean;
};

const BookingBoxAside = ({ activator, data }: Props) => {

  const intl = useIntl();
  const navigate = useLocaleNavigate();
  const { print } = useLocaleFormatter();
  const { format } = useTools();

  const [ period ] = usePeriodParams();
  const [ { [ArticleType.RENTAL]: { saving: ctx } } ] = useSearchContext();
  const initedGuests = data?.touristTaxConfig ? (ctx as CustomRentalAccommodationFilterInput).guests : undefined;

  const [ open, setOpen ] = useState<boolean>(false);
  const [ slideItem, setSlideItem ] = useState<number>(1);

  const [ choice, setChoice ] = useState<BookingOption<Moment> | undefined>(undefined);
  const [ guests, setGuests ] = useState<Record<AgeCategory, number> | undefined>(initedGuests);

  const [ getPrice ] = useLazyQuery<{ option: any }>(PRICE_GQL_DATA, { fetchPolicy: "no-cache" });

  useEffect(() => {
    if (!period) {
      setChoice(undefined);
      return;
    }
    const invalid = () => setChoice({ period: castPeriod(period), bookable: false, price: "onDemand" });
    getPrice({ variables: { articleId: data.articleId, period: toGQLPeriod(period) }})
      .then(({ data }) => !!data?.option ? setChoice(castBookingOption(data!.option)) : invalid())
      .catch(invalid);

  }, [ period ]);

  const choiceWithTaxes = useMemo<BookingOption<Moment> | undefined>(() => {
    if (!data.touristTaxConfig || !guests || !choice || choice.price === "onDemand")
      return choice;

    const nights = format.nights(choice.period);
    const nightsHasExtras = [
      {
        type: CartOptionType.TOURIST_TAX,
        amount: {
          amount: Object.entries(guests).reduce((acc, [ ageCategory, count ]) => {
            const byNight = data.touristTaxConfig!.grid.find(gi => gi.ageCategory === ageCategory);
            return acc + (!byNight ? 0 : (castPrice(byNight.price).amount * nights * count));}, 0),
          currency: castPrice(choice.price.amount).currency
        },
        data: {
          optionId: data.touristTaxConfig!.optionId,
          participantsCount: Object.entries(guests).reduce<any>((acc, [ ageCategory, number ]) => {
            acc.push({ ageCategory, number });
            return acc;}, [])
        }
      }
    ];

    return calculateBookingOptionWithExtras(choice, nightsHasExtras) as BookingOption<Moment>;
  }, [ choice, guests, data.touristTaxConfig ]);


  // dom interactions

  const onAction = (option?: BookingOption<Moment>) => {
    if (!option || !option.bookable) {
      setSlideItem(2);
      return
    }
    navigate(`/checkout/?${checkoutTools.rebuild(data.articleId, option).toString()}`);
  }


  //rendering

  const buttonLabel = useMemo(() => choice?.bookable ? actions.book : transDefs.contact, [ choice ]);
  const buttonDisabled = useMemo(() => !!data.touristTaxConfig && choice?.bookable && !guests,
    [ choice, data.touristTaxConfig, guests ]);

  return (
    <Overlay.StickableBottom {...{ activator, open, setOpen }}>

      <Overlay.StickableContent title={intl.formatMessage(actions.bookHoliday)}>
        <Display.Slider className="article-asidable booking-box"
                        displayedIndex={slideItem} animated={false}>

          <Display.SliderItem className="selector">
            {(() => {
              const content: Record<string, ContentRender> = {
                period: {
                  title: intl.formatMessage(booking.period),
                  children: <PeriodInput sticked={activator}
                                         config={data?.config}
                                         value={choice}
                                         onChange={setChoice} />
                },
                ...(!!data.touristTaxConfig) && {
                  guests: {
                    title: intl.formatMessage(form.guests),
                    children: <GuestsInput sticked={activator}
                                           maxGuests={data?.maxGuests}
                                           value={guests}
                                           onChange={setGuests} />
                  }
                }
              };
              if (activator)
                return (
                  <Display.Collapse autoClose={true} animated={false}>
                    {Object.values(content).map((props, index) =>
                      <Display.CollapseItem key={index} {...props} open={index === 0} />
                    )}
                  </Display.Collapse>);

              return Object.values(content).map(({ children }, index) => (<Fragment key={index}>{children}</Fragment>));
            })()}

            {!!choiceWithTaxes &&
              <dl className="price-resume">
                <dt className="total">
                  {`${intl.formatMessage(pricing.total)} ${intl.formatMessage(booking.nights, { count: format.nights(choiceWithTaxes.period) })}`}
                </dt>
                <dd className="total">
                  {choiceWithTaxes.price === "onDemand"
                    ? intl.formatMessage(pricing.onDemand)
                    : print.price(choiceWithTaxes.price.amount)
                  }
                </dd>
              </dl>
            }

            <Button type="button" className="bold with-icon"
                    disabled={buttonDisabled}
                    onClick={() => onAction(choiceWithTaxes)}>
              {intl.formatMessage(buttonLabel)}
              <IconChevronRight />
            </Button>

            <>
              {buttonDisabled &&
                <p className="textual-help">{intl.formatMessage(transDefs.invalid)}</p>
              }
              {(!!choice && !choice.bookable) &&
                <p className="textual-help on-demand">{intl.formatMessage(transDefs.onDemand)}</p>
              }
            </>
          </Display.SliderItem>

          <Display.SliderItem className="contact">
            <Button type="button" className="back link no-border with-icon"
                    onClick={() => setSlideItem(1)}>
              <IconChevronLeft />
              {intl.formatMessage(actions.back)}
            </Button>
            <ContactForm {...(!!data?.articleId) && {
              data: {
                articleId: data.articleId,
                ...(!!choice) && { period: choice.period }
              }
            }} />
          </Display.SliderItem>

        </Display.Slider>
      </Overlay.StickableContent>

      <Overlay.StickableResume className="booking"
                               icon={IconCalendarPlus}
                               asFloatingRef={true}>
        {!choiceWithTaxes &&
          <>
            <span className="price ellipsed">{format.anyPrice(data.lowestPrice)}</span>
            <Button type="button" className="light small lower">
              {intl.formatMessage(transDefs.see)}
            </Button>
          </>
        }
        {!!choiceWithTaxes &&
          <>
            <span className="price">
              {choiceWithTaxes.price === "onDemand"
                ? intl.formatMessage(pricing.onDemand)
                : print.price(choiceWithTaxes.price.amount)
              }
            </span>
            <span className="period">
              {print.period(choiceWithTaxes.period, true, true)}
            </span>
            <Button type="button" className="light small lower">
              {intl.formatMessage(actions.continue)}
            </Button>
          </>
        }
      </Overlay.StickableResume>

    </Overlay.StickableBottom>);

}

export default BookingBoxAside;
